import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import UploadSpreadsheetModalPane from 'bulk-import/panes/UploadSpreadsheetModalPane';
import * as SpreadsheetBulkImportActions from 'bulk-import/SpreadsheetBulkImportActions';
import {IMPORT_TYPE, Pane, PaneKey} from 'bulk-import/SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from 'bulk-import/SpreadsheetBulkImportModalContainer';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {Row, Table, TableHeaderControlsArgs} from 'notebook/tables/TableConstants';
import {AssayRunSchema} from 'results/assay-schemas/AssaySchemaConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';

import {
  automationFileConfigStyles,
  AutomationOutputFileConfigEditorProps,
} from '../AutomationFileConfigUtils';
import {IconButton} from '../RunSchemaEditorActionButton';
import {ClientAutomationOutputFileConfigSpec} from '../RunSchemaSpecConstants';
import {
  convertStringToColumnTypeMetadatas,
  DatasetColumnTypeMetadata,
  PRIMITIVE_COLUMN_TYPE_TO_DISPLAY_NAME,
} from './DatasetConfigConstants';

// Set to fit all column
const DEFAULT_COLUMN_WIDTH = 180;

// exported for test
export const DELETE_DATA_TEST_ATTRS = {
  delete: {component: 'DatasetConfigEditor', element: 'delete'},
};
export const EDIT_DATA_TEST_ATTRS = {
  edit: {component: 'DatasetConfigEditor', element: 'edit'},
};

function _getTableName(columnTypeMetadatas: Array<DatasetColumnTypeMetadata>): string {
  return `${columnTypeMetadatas.length} columns mapped`;
}

function _createColumnLabelsForDatasetColumns(
  columnTypeMetadatas: Array<DatasetColumnTypeMetadata>
): Array<string> {
  return columnTypeMetadatas.map((metadata) => metadata.name ?? '');
}

function _createTableRowForDatasetColumns(columnTypeMetadatas: Array<DatasetColumnTypeMetadata>): Row {
  return columnTypeMetadatas.map((metadata) => ({
    text: metadata.columnType ? PRIMITIVE_COLUMN_TYPE_TO_DISPLAY_NAME[metadata.columnType] : '',
  }));
}

function _createDatasetColumnsTable(outputFileConfig: ClientAutomationOutputFileConfigSpec): Table {
  const columnTypeMetadatas: Array<DatasetColumnTypeMetadata> = convertStringToColumnTypeMetadatas(
    outputFileConfig.datasetConfigJson
  );
  const columnWidths: Array<number> = columnTypeMetadatas.map(() => DEFAULT_COLUMN_WIDTH);

  return {
    name: _getTableName(columnTypeMetadatas),
    table: [_createTableRowForDatasetColumns(columnTypeMetadatas)],
    widths: columnWidths,
    columnConstraints: [],
    headerRow: false,
    headerCol: false,
    columnLabels: _createColumnLabelsForDatasetColumns(columnTypeMetadatas),
    lockedDims: true,
    allowColumnEdits: false,
    _hideRename: true,
  };
}

function getPanes(args: DatasetConfigModalArgs): Array<Pane> {
  return [
    {
      paneKey: PaneKey.CONNECTION_FILES,
      configuration: {
        nextPaneKey: PaneKey.DATASET_CONFIG,
        adapterId: args.baseSchema?.adapterId ?? undefined,
        delimiter: args.outputFileConfig.delimiter,
        automationOutputFileConfig: args.outputFileConfig,
      },
    },
    {
      paneKey: PaneKey.UPLOAD_SPREADSHEET,
      configuration: UploadSpreadsheetModalPane.configure({hideBackButton: true}),
    },
    {
      paneKey: PaneKey.DATASET_CONFIG,
      configuration: {
        outputFileConfig: args.outputFileConfig,
        goToImportConnectionFiles: SpreadsheetBulkImportActions.goToPaneWithKey.bind(
          null,
          PaneKey.CONNECTION_FILES
        ),
        goToUploadSpreadsheet: SpreadsheetBulkImportActions.goToPaneWithKey.bind(
          null,
          PaneKey.UPLOAD_SPREADSHEET
        ),
        onUpdateConfig: args.onUpdateConfig,
      },
    },
  ];
}

interface DatasetConfigModalArgs {
  outputFileConfig: ClientAutomationOutputFileConfigSpec;
  baseSchema: AssayRunSchema | null;
  onUpdateConfig: (newConfig: ClientAutomationOutputFileConfigSpec) => void;
  ownerId: string;
  shouldShowUploadedFilesOnly?: boolean;
}

export function openDatasetConfigModal(args: DatasetConfigModalArgs): void {
  legacyRunModal(SpreadsheetBulkImportModalContainer, {
    onImportFromSpreadsheet: () => {},
    onRequestClose: SpreadsheetBulkImportActions.closeModal,
    importType: IMPORT_TYPE.EXAMPLE_FILE,
    importItemWord: 'row of output',
    title: 'Configure dataset',
    panes: getPanes(args),
    initialSettings: {
      initialPaneKey: PaneKey.DATASET_CONFIG,
    },
    inferDataTypes: true,
  });
}

export default function DatasetConfigEditor(
  props: AutomationOutputFileConfigEditorProps
): JSX.Element | null {
  if (!props.outputFileConfig.datasetConfigJson) {
    return (
      <div className={css(automationFileConfigStyles.sectionDescriptionAbove)}>
        <a
          className={css(automationFileConfigStyles.formatSectionItem)}
          onClick={() => {
            openDatasetConfigModal(props);
          }}
          {...getTestProps({
            component: 'DatasetConfigEditor',
            element: 'action',
          })}
        >
          Configure dataset
        </a>
      </div>
    );
  }

  const shouldPreventDelete =
    props.outputFileConfig.shouldCreateDataset && props.outputFileConfig.resultSchemaInfo?.schema;
  const {datasetConfigJson, ...updatedConfig} = props.outputFileConfig;

  const renderHeaderControls = ({isCollapsed}: TableHeaderControlsArgs): JSX.Element | null => {
    if (isCollapsed) {
      return null;
    }
    return (
      <div>
        {!shouldPreventDelete && (
          <IconButton
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            className="u-mini-spacing-right"
            icon="delete"
            onClick={() => {
              props.onUpdateConfig(updatedConfig);
            }}
            dataTestAttrs={DELETE_DATA_TEST_ATTRS.delete}
          />
        )}
        <IconButton
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          icon="edit"
          onClick={() => {
            openDatasetConfigModal(props);
          }}
          dataTestAttrs={EDIT_DATA_TEST_ATTRS.edit}
        />
      </div>
    );
  };

  return (
    <div className={`simpleDivTable-row mediocre ${css(styles.columnTableContainer, styles.divTableRow)}`}>
      <SimpleTableEditable
        {...standaloneProps}
        table={_createDatasetColumnsTable(props.outputFileConfig)}
        cid="dataset-config-columns-1"
        hideFormulaBox={true}
        isCompactBottomSpacing={true}
        locked={true}
        headerControls={renderHeaderControls}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  columnTableContainer: {
    display: 'inline-block',
  },
  divTableRow: {
    ':hover': {
      background: 'white',
    },
    borderTop: 'none',
    padding: 0,
  },
});
